import { supabase } from '../lib/supabase';

// Map DB row (public.campaign_links) to normalized shape used by the UI
const normalizeLink = (row) => {
  if (!row) return null;
  return {
    id: row?.id,
    slug: row?.slug || '',
    youtube_music_url: row?.youtube_music_url || '',
    spotify_url: row?.spotify_url || '',
    apple_music_url: row?.apple_music_url || '',
    tiktok_sound_url: row?.tiktok_sound_url || '',
    campaign_slug: row?.campaign_slug || row?.slug || '',
    campaign_id: row?.campaign_id || null,
    influencer_id: row?.influencer_id || null,
    is_active: row?.is_active ?? true,
    // Pass through any extra fields (title, artist_name, cover_image_url, user_id, created_at, etc.)
    ...Object.fromEntries(
      Object.entries(row)?.filter(([k]) =>
        !['slug', 'campaign_slug', 'campaign_id', 'is_active']?.includes(k)
      )
    ),
  };
};

// Fetch a campaign link by slug (public)
// Queries public.campaign_links — tries `slug` first, then falls back to `campaign_slug`
// Only returns rows where is_active = true
export const getCampaignLinkBySlug = async (slug) => {
  const FIELDS = 'slug,campaign_slug,campaign_id,influencer_id,spotify_url,apple_music_url,youtube_music_url,tiktok_sound_url,is_active';

  try {
    // Try `slug` column first
    let { data, error } = await supabase
      ?.from('campaign_links')
      ?.select(FIELDS)
      ?.eq('slug', slug)
      ?.eq('is_active', true)
      ?.maybeSingle();

    if (error) throw error;

    // Fallback: try `campaign_slug` if not found by slug
    if (!data) {
      const fallback = await supabase
        ?.from('campaign_links')
        ?.select(FIELDS)
        ?.eq('campaign_slug', slug)
        ?.eq('is_active', true)
        ?.maybeSingle();

      if (fallback?.error) throw fallback?.error;
      data = fallback?.data;
    }

    return { data: normalizeLink(data), error: null };
  } catch (error) {
    console.error('Error fetching campaign link:', error?.message);
    return { data: null, error };
  }
};

// Track a click event (public, no auth required)
export const trackClick = async ({ campaignLinkId, campaignId, influencerId, platform, device, country }) => {
  try {
    const { error } = await supabase?.from('link_clicks')?.insert({
      campaign_link_id: campaignLinkId,
      campaign_id: campaignId || null,
      influencer_id: influencerId || null,
      platform,
      device: device || 'unknown',
      country: country || 'unknown',
      user_agent: navigator?.userAgent || '',
      clicked_at: new Date()?.toISOString(),
    });

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error tracking click:', error?.message);
    return { error };
  }
};

// Get all campaign links for the current user
export const getUserCampaignLinks = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('campaign_links')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return { data: (data || [])?.map(normalizeLink), error: null };
  } catch (error) {
    console.error('Error fetching user campaign links:', error?.message);
    return { data: [], error };
  }
};

// Create a new campaign link
export const createCampaignLink = async (userId, linkData) => {
  try {
    const slug = linkData?.campaignSlug || generateSlug(linkData?.title);
    const { data, error } = await supabase
      ?.from('campaign_links')
      ?.insert({
        user_id: userId,
        influencer_id: linkData?.influencerId || null,
        campaign_id: linkData?.campaignId || null,
        slug: slug,
        campaign_slug: slug,
        title: linkData?.title,
        artist_name: linkData?.artistName || '',
        cover_image_url: linkData?.coverImageUrl || '',
        spotify_url: linkData?.spotifyUrl || '',
        apple_music_url: linkData?.appleMusicUrl || '',
        youtube_music_url: linkData?.youtubeMusicUrl || '',
        tiktok_sound_url: linkData?.tiktokSoundUrl || '',
        is_active: true,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: normalizeLink(data), error: null };
  } catch (error) {
    console.error('Error creating campaign link:', error?.message);
    return { data: null, error };
  }
};

// Update a campaign link
export const updateCampaignLink = async (linkId, userId, updates) => {
  try {
    const { data, error } = await supabase
      ?.from('campaign_links')
      ?.update({
        title: updates?.title,
        artist_name: updates?.artistName,
        cover_image_url: updates?.coverImageUrl,
        spotify_url: updates?.spotifyUrl,
        apple_music_url: updates?.appleMusicUrl,
        youtube_music_url: updates?.youtubeMusicUrl,
        tiktok_sound_url: updates?.tiktokSoundUrl,
        is_active: updates?.isActive,
      })
      ?.eq('id', linkId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) throw error;
    return { data: normalizeLink(data), error: null };
  } catch (error) {
    console.error('Error updating campaign link:', error?.message);
    return { data: null, error };
  }
};

// Delete a campaign link
export const deleteCampaignLink = async (linkId, userId) => {
  try {
    const { error } = await supabase
      ?.from('campaign_links')
      ?.delete()
      ?.eq('id', linkId)
      ?.eq('user_id', userId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting campaign link:', error?.message);
    return { error };
  }
};

// Get analytics for a specific campaign link
export const getLinkAnalytics = async (userId) => {
  try {
    // Get all links for user
    const { data: rawLinks, error: linksError } = await supabase
      ?.from('campaign_links')
      ?.select('id, slug, campaign_slug, title, artist_name, influencer_id, created_at')
      ?.eq('user_id', userId)
      ?.eq('is_active', true);

    if (linksError) throw linksError;

    const links = (rawLinks || [])?.map(row => ({
      ...row,
      campaign_slug: row?.campaign_slug || row?.slug || '',
    }));

    if (!links || links?.length === 0) {
      return { data: { links: [], clicks: [], totalClicks: 0, platformBreakdown: {}, influencerPerformance: [] }, error: null };
    }

    const linkIds = links?.map(l => l?.id);

    // Get all clicks for these links
    const { data: clicks, error: clicksError } = await supabase
      ?.from('link_clicks')
      ?.select('*')
      ?.in('campaign_link_id', linkIds)
      ?.order('clicked_at', { ascending: false });

    if (clicksError) throw clicksError;

    const allClicks = clicks || [];

    // Platform breakdown
    const platformBreakdown = allClicks?.reduce((acc, click) => {
      acc[click.platform] = (acc?.[click?.platform] || 0) + 1;
      return acc;
    }, {});

    // Per-link click counts
    const clicksByLink = allClicks?.reduce((acc, click) => {
      acc[click.campaign_link_id] = (acc?.[click?.campaign_link_id] || 0) + 1;
      return acc;
    }, {});

    // Influencer performance
    const influencerClicks = allClicks?.reduce((acc, click) => {
      if (click?.influencer_id) {
        if (!acc?.[click?.influencer_id]) {
          acc[click.influencer_id] = { clicks: 0, platforms: {} };
        }
        acc[click.influencer_id].clicks += 1;
        acc[click.influencer_id].platforms[click.platform] = (acc?.[click?.influencer_id]?.platforms?.[click?.platform] || 0) + 1;
      }
      return acc;
    }, {});

    const linksWithStats = links?.map(link => ({
      ...link,
      totalClicks: clicksByLink?.[link?.id] || 0,
    }));

    return {
      data: {
        links: linksWithStats,
        clicks: allClicks,
        totalClicks: allClicks?.length,
        platformBreakdown,
        influencerPerformance: Object.entries(influencerClicks)?.map(([id, stats]) => ({ influencerId: id, ...stats })),
      },
      error: null,
    };
  } catch (error) {
    console.error('Error fetching link analytics:', error?.message);
    return { data: null, error };
  }
};

// Detect device type from user agent
export const detectDevice = () => {
  const ua = navigator?.userAgent || '';
  if (/mobile/i?.test(ua)) return 'mobile';
  if (/tablet|ipad/i?.test(ua)) return 'tablet';
  return 'desktop';
};

// Detect country via free IP geolocation (ipapi.co)
export const detectCountry = async () => {
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
    if (!res?.ok) return 'unknown';
    const json = await res?.json();
    return json?.country_code || json?.country || 'unknown';
  } catch {
    return 'unknown';
  }
};

// Generate a URL-safe slug from a title
export const generateSlug = (title) => {
  return title?.toLowerCase()?.replace(/[^a-z0-9\s-]/g, '')?.replace(/\s+/g, '-')?.replace(/-+/g, '-')?.trim()
    + '-' + Math.random()?.toString(36)?.substring(2, 7);
};
